import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import {
  decodeMnemonicPhrase,
  encodeMnemonicPhrase,
  normalizeMnemonicPhrase
} from './identityMnemonic.js';

const DATABASE_NAME = 'dechess';
const DATABASE_VERSION = 1;
const STORE_NAME = 'identity';
const IDENTITY_KEY = 'primary';
/**
 * PeerJS session suffix.
 * - Board URLs (`?game=`) keep the session so challenge peerIds stay dialable.
 * - Lobby loads always mint a fresh session so leaving a board / hard refresh
 *   does not wait on "ID is taken" while the prior socket drains.
 * - Hard reload always mints, even on a board URL.
 */
const PEER_SESSION_KEY = 'dechess-peer-session-v1';

function mintPeerSessionId() {
  return crypto.randomUUID().replaceAll('-', '').slice(0, 10);
}

function isDocumentReload() {
  try {
    const nav = performance.getEntriesByType?.('navigation')?.[0];
    if (nav?.type === 'reload') {
      return true;
    }
  } catch {
    // Ignore Performance API gaps.
  }
  try {
    // Legacy fallback (Safari).
    return Number(performance?.navigation?.type) === 1;
  } catch {
    return false;
  }
}

function hasActiveGameInUrl() {
  try {
    if (typeof window === 'undefined') {
      return false;
    }
    return Boolean(new URLSearchParams(window.location.search).get('game'));
  } catch {
    return false;
  }
}

function resolvePageSessionId() {
  try {
    const existing = sessionStorage.getItem(PEER_SESSION_KEY);
    // Only board tabs reuse the session (challenge join peer continuity).
    const reuseBoardSession = !isDocumentReload()
      && hasActiveGameInUrl()
      && existing
      && /^[a-f0-9]{10}$/i.test(existing);
    if (reuseBoardSession) {
      return existing.toLowerCase();
    }
    const next = mintPeerSessionId();
    sessionStorage.setItem(PEER_SESSION_KEY, next);
    return next;
  } catch {
    return mintPeerSessionId();
  }
}

let pageSessionId = resolvePageSessionId();

/** Current PeerJS session suffix (10 hex chars). */
export function getPeerSessionId() {
  return pageSessionId;
}

/** Mint a new PeerJS session (e.g. after unavailable-id / "ID is taken"). */
export function rotatePeerSessionId() {
  const next = mintPeerSessionId();
  pageSessionId = next;
  try {
    sessionStorage.setItem(PEER_SESSION_KEY, next);
  } catch {
    // Private mode / quota — in-memory id still works for this page life.
  }
  return next;
}

export function isPeerIdTakenError(error) {
  const message = String(error?.message || error || '');
  const type = String(error?.type || error?.name || '');
  return /unavailable-id/i.test(type)
    || /unavailable-id/i.test(message)
    || /id .* is taken/i.test(message)
    || /is taken/i.test(message);
}

function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function serializeKeyPair(keyPair) {
  return {
    signingSecretKey: naclUtil.encodeBase64(keyPair.signing.secretKey),
    signingPublicKey: naclUtil.encodeBase64(keyPair.signing.publicKey),
    encryptionSecretKey: naclUtil.encodeBase64(keyPair.encryption.secretKey),
    encryptionPublicKey: naclUtil.encodeBase64(keyPair.encryption.publicKey)
  };
}

function deserializeKeyPair(record) {
  return {
    signing: {
      secretKey: naclUtil.decodeBase64(record.signingSecretKey),
      publicKey: naclUtil.decodeBase64(record.signingPublicKey)
    },
    encryption: {
      secretKey: naclUtil.decodeBase64(record.encryptionSecretKey),
      publicKey: naclUtil.decodeBase64(record.encryptionPublicKey)
    }
  };
}

function createKeyPair() {
  return {
    signing: nacl.sign.keyPair(),
    encryption: nacl.box.keyPair()
  };
}

function keyPairToPublicBundle(keyPair) {
  return {
    signingPublicKey: naclUtil.encodeBase64(keyPair.signing.publicKey),
    encryptionPublicKey: naclUtil.encodeBase64(keyPair.encryption.publicKey)
  };
}

function exportPayloadFromKeyPair(keyPair) {
  const payload = new Uint8Array(64);
  payload.set(keyPair.signing.secretKey.slice(0, 32), 0);
  payload.set(keyPair.encryption.secretKey, 32);
  return payload;
}

function keyPairFromExportPayload(payload) {
  if (!(payload instanceof Uint8Array) || payload.length !== 64) {
    throw new Error('Invalid identity export payload');
  }
  return {
    signing: nacl.sign.keyPair.fromSeed(payload.slice(0, 32)),
    encryption: nacl.box.keyPair.fromSecretKey(payload.slice(32, 64))
  };
}

async function saveKeyPair(keyPair) {
  const database = await openDatabase();

  try {
    await new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      store.put(
        {
          ...serializeKeyPair(keyPair),
          createdAt: new Date().toISOString()
        },
        IDENTITY_KEY
      );
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally {
    database.close();
  }
}

async function readStoredKeyPair() {
  const database = await openDatabase();

  try {
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readonly');
      const request = transaction.objectStore(STORE_NAME).get(IDENTITY_KEY);
      request.onsuccess = () => {
        if (!request.result) {
          resolve(null);
          return;
        }
        resolve(deserializeKeyPair(request.result));
      };
      request.onerror = () => reject(request.error);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally {
    database.close();
  }
}

function identityFromKeyPair(keyPair) {
  return identityFingerprint(keyPair.signing.publicKey).then((fingerprint) => ({
    keyPair,
    publicKey: keyPairToPublicBundle(keyPair),
    playerId: `dc-${fingerprint.slice(0, 16)}`,
    peerId: `dc-${fingerprint.slice(0, 16)}-${pageSessionId}`
  }));
}

/** Rebuild identity with a freshly rotated PeerJS session suffix. */
export async function rotateIdentityPeerSession(identity) {
  if (!identity?.keyPair) {
    throw new Error('Cannot rotate peer session without a loaded identity');
  }
  rotatePeerSessionId();
  return identityFromKeyPair(identity.keyPair);
}

async function loadOrCreateKeyPair() {
  const database = await openDatabase();

  try {
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(IDENTITY_KEY);
      let keyPair;

      request.onsuccess = () => {
        if (request.result) {
          keyPair = deserializeKeyPair(request.result);
          return;
        }

        keyPair = createKeyPair();
        store.put(
          {
            ...serializeKeyPair(keyPair),
            createdAt: new Date().toISOString()
          },
          IDENTITY_KEY
        );
      };
      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => resolve(keyPair);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally {
    database.close();
  }
}

function toHex(bytes) {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function identityFingerprint(publicKey) {
  const digest = await crypto.subtle.digest('SHA-256', publicKey);
  return toHex(new Uint8Array(digest));
}

export async function loadIdentity() {
  const keyPair = await loadOrCreateKeyPair();
  return identityFromKeyPair(keyPair);
}

export async function exportIdentityMnemonicPhrase() {
  const keyPair = await readStoredKeyPair();
  if (!keyPair) {
    throw new Error('No local identity is available to export');
  }
  const words = await encodeMnemonicPhrase(exportPayloadFromKeyPair(keyPair));
  return words.join(' ');
}

export async function importIdentityMnemonicPhrase(phrase) {
  const words = normalizeMnemonicPhrase(phrase);
  const payload = await decodeMnemonicPhrase(words);
  const keyPair = keyPairFromExportPayload(payload);
  await saveKeyPair(keyPair);
  return identityFromKeyPair(keyPair);
}

export async function clearStoredIdentity() {
  const database = await openDatabase();

  try {
    await new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      const request = transaction.objectStore(STORE_NAME).delete(IDENTITY_KEY);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally {
    database.close();
  }
}
